import { LNGS_NAMESPACE_MODULES } from '@ingenico/util-i18n-config';
import { logError } from '@ingenico/util-common';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface UseParameterModuleContentParametersModalParams {
  readonly onConfirm?: () => Promise<void>;
  readonly onClose?: () => void;
  readonly mode?: 'confirm' | 'view';
  readonly onError?: (error: Error) => void;
}

export function useParameterModuleContentParametersModal({
  onConfirm,
  mode,
  onClose,
  onError,
}: UseParameterModuleContentParametersModalParams) {
  const { t } = useTranslation([LNGS_NAMESPACE_MODULES.COMMON, LNGS_NAMESPACE_MODULES.DEFAULT]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasErrors, setHasErrors] = useState<boolean>(false);

  const handleConfirm = useCallback(() => {
    setIsLoading(true);
    Promise.resolve(void 0)
      .then(() => onConfirm?.() ?? void 0)
      .finally(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        const modalError = error as Error;

        onError?.(modalError);
        logError(modalError);
        setHasErrors(true);
      });
  }, [onConfirm, onError]);

  const title = useMemo(() => t('title.modal.reviewChanges', { ns: LNGS_NAMESPACE_MODULES.DEFAULT }), [t]);
  const subtitle = useMemo(() => t('subtitle.modal.reviewChanges', { ns: LNGS_NAMESPACE_MODULES.DEFAULT }), [t]);
  const cancelButtonLabel = useMemo(
    () =>
      mode === 'confirm'
        ? t('button.cancel', { ns: LNGS_NAMESPACE_MODULES.COMMON })
        : t('button.close', { ns: LNGS_NAMESPACE_MODULES.COMMON }),
    [t, mode],
  );
  const confirmButtonLabel = useMemo(
    () =>
      hasErrors
        ? t('button.retry', { ns: LNGS_NAMESPACE_MODULES.COMMON })
        : t('button.confirm', { ns: LNGS_NAMESPACE_MODULES.COMMON }),
    [t, hasErrors],
  );
  const cancelParameterLabel = useMemo(() => t('button.cancel', { ns: LNGS_NAMESPACE_MODULES.COMMON }), [t]);
  const changesLabel = useMemo(() => t('field.changes', { ns: LNGS_NAMESPACE_MODULES.COMMON }), [t]);

  const shouldHideConfirmButton = useMemo(() => mode !== 'confirm', [mode]);

  const handleClose = useCallback(() => {
    setHasErrors(false);
    onClose?.();
  }, [onClose]);

  return {
    title,
    subtitle,
    cancelButtonLabel,
    confirmButtonLabel,
    handleConfirm,
    isLoading,
    shouldHideConfirmButton,
    cancelParameterLabel,
    changesLabel,
    hasErrors,
    handleClose,
  };
}
